import { IGender, ISmsRecord, LivePlannedEvents, Metrics, record } from '@admin/interfaces/metric.interface'
import { MetricsService } from '@admin/services/metrics.service'
import { CommonModule } from '@angular/common'
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { DialogComponent } from '@app/modules/shared/components/dialog/dialog.component'
import { DialogData } from '@app/modules/shared/components/dialog/dialog.interface'
import { ToastrService } from '@app/services/toastr.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { getYear } from 'date-fns'
import { Subject, Subscription } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'
import { AddNameDialogComponent } from '../add-name-dialog/add-name-dialog.component'

/**
 * Displays relevant statistics to the Super Admins
 */
@Component({
  selector: 'ngx-statistics',
  templateUrl: './statistics.component.html',
  styleUrls: ['./statistics.component.scss'],
  imports: [CommonModule, MatTabsModule, FormsModule]
})
export class StatisticsComponent implements OnInit, OnDestroy, OnChanges {
  // Input from parent component
  @Input() selectedTabIndex?: number

  // Tab state
  currentTabIndex = 0

  // Trusted List state
  public genderList: IGender[] = []
  public originalGenderList: IGender[] = []
  public showDeleteConfirmation = false
  private itemToDelete: number
  public searchTerm = ''
  searchTerm$ = new Subject<string>()
  private searchSubscription: Subscription
  public itemsPerPage = 30
  public currentPage = 1
  public lastPage: number

  // Events and Workspaces state
  public year = getYear(Date.now())
  public months = ['Total', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  public result: Metrics = []

  // Live and Planned Events state
  public liveAndplannedEvents: record[] = []
  public plannedEventCount = 0
  public liveEventsCount = 0
  public allEvents: record[] = []
  public paymentFilter = ''
  public currentFilter = 'All'

  // SMS Events state
  public smsEvents: ISmsRecord[] = []
  private refreshingSmsKeys = new Set<string>()
  private _destroy$ = new Subject<void>()

  /**
   * Creates the component and injects it's dependencies
   * @param service MetricsService
   */
  constructor(private service: MetricsService, private auth: AuthService, private alertService: AlertService, private toastr: ToastrService, public dialog: MatDialog) {
    this.initializeSearchSubscription()
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedTabIndex'] && changes['selectedTabIndex'].currentValue !== undefined) {
      this.currentTabIndex = changes['selectedTabIndex'].currentValue
      this.setActiveTab(this.currentTabIndex)
    }
  }

  /**
   * Initialize component
   */
  ngOnInit(): void {
    this.searchSubscription = this.searchTerm$.pipe(debounceTime(300), takeUntil(this._destroy$)).subscribe((term) => {
      this.search()
    })

    this.getGenderList()
    this.search() // Load initial data for first tab
  }

  /**
   * Clean up subscriptions
   */
  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe()
    }
    this._destroy$.next()
    this._destroy$.complete()
  }

  /**
   * Initialize the search subscription with debounce
   */
  private initializeSearchSubscription(): void {
    this.searchSubscription = this.searchTerm$.pipe(debounceTime(500), takeUntil(this._destroy$)).subscribe((searchTerm) => this.searchGenderList(searchTerm))
  }

  /**
   * Handle tab changes
   * @param event MatTabChangeEvent containing the selected tab index
   */
  onTabChange(event: MatTabChangeEvent): void {
    this.currentTabIndex = event.index
    this.resetList()

    // Load data based on selected tab
    switch (this.currentTabIndex) {
      case 0: // Created Events
      case 1: // Created Workspaces
        this.search()
        break
      case 2: // Trusted List
        this.getGenderList()
        break
      case 3: // Live & Planned Events
        this.loadLiveAndPlannedEvents()
        break
      case 4: // Events with SMS
        this.getSmsEvents()
        break
    }
  }

  /**
   * Set active tab for modern navigation
   * @param tabIndex The index of the tab to activate
   */
  setActiveTab(tabIndex: number): void {
    this.currentTabIndex = tabIndex
    this.resetList()

    // Load data based on selected tab
    switch (this.currentTabIndex) {
      case 0: // Created Events
      case 1: // Created Workspaces
        this.search()
        break
      case 2: // Trusted List
        this.getGenderList()
        break
      case 3: // Live & Planned Events
        this.loadLiveAndPlannedEvents()
        break
      case 4: // Events with SMS
        this.getSmsEvents()
        break
    }
  }

  /**
   * Reset all lists when switching tabs
   */
  resetList(): void {
    this.result = []
    this.smsEvents = []
    this.liveAndplannedEvents = []
    this.allEvents = []
  }

  /**
   * Load live and planned events data
   */
  public loadLiveAndPlannedEvents(): void {
    this.service
      .totalLiveAndPlannedEvents(this.year)
      .pipe(takeUntil(this._destroy$))
      .subscribe((result: LivePlannedEvents) => {
        this.allEvents = [...result.data]
        this.liveAndplannedEvents = [...result.data]
        this.liveEventsCount = result.data.filter((record) => record.status === 'Live').length
        this.plannedEventCount = result.data.filter((record) => record.status === 'Upcoming').length
        this.liveAndplannedEvents.sort((a, b) => (new Date(a.start_date) as any) - (new Date(b.start_date) as any))
      })
  }

  /**
   * Search for metrics based on current tab and year
   */
  search(): void {
    if (this.year < 2018 || this.year > 2099) {
      if (this.year < 2018) {
        this.toastr.error('Error', 'The year must be more than 2017')
      } else {
        this.toastr.error('Error', 'Please enter a valid year')
      }
      this.year = new Date().getFullYear()
      return
    }

    const mode = this.currentTabIndex === 0 ? 'Events' : 'Workspaces'

    this.service['total' + mode](this.year)
      .pipe(takeUntil(this._destroy$))
      .subscribe((result: Metrics) => {
        this.result = result
        // Add totals row
        this.result.push({
          month: 0,
          count: result.reduce((b, c) => (b += c.count), 0),
          archivedCount: mode === 'Events' ? result.reduce((p, c) => p + c.archivedCount, 0) : 0,
          year: this.year
        })
        // Add month names
        this.result.forEach((item) => {
          item.displayMonth = this.months[item.month]
        })
      })
  }

  /**
   * Get Events with SMS status
   */
  getSmsEvents(): void {
    this.service
      .getSmsEvents(this.year)
      .pipe(takeUntil(this._destroy$))
      .subscribe(({ data }) => {
        this.smsEvents = data
      })
  }

  /**
   * Refreshes SMS count for a single event
   * @param event SMS event record
   * @param index Index in smsEvents array
   */
  refreshSmsCount(event: ISmsRecord, index: number): void {
    const workspaceSlug = this.auth.currentWorkspace?.slug
    const eventSlug = event.slug

    if (!workspaceSlug) {
      this.alertService.alert(Alert.ERROR, 'No workspace selected')
      return
    }

    if (!eventSlug) {
      this.alertService.alert(Alert.ERROR, 'Missing event slug')
      return
    }

    if (this.refreshingSmsKeys.has(eventSlug)) {
      return
    }

    this.refreshingSmsKeys.add(eventSlug)
    this.service.refreshSmsMetrics(workspaceSlug, eventSlug).subscribe({
      next: (res) => {
        this.alertService.alert(Alert.SUCCESS, res.message)
        this.refreshingSmsKeys.delete(eventSlug)
      },
      error: () => {
        this.alertService.alert(Alert.ERROR, 'Failed to refresh SMS count')
        this.refreshingSmsKeys.delete(eventSlug)
      }
    })
  }

  /**
   * Checks if SMS metrics are being refreshed for an event
   * @param event SMS event record
   * @returns true when refresh is in progress
   */
  isRefreshingSms(event: ISmsRecord): boolean {
    return event.slug ? this.refreshingSmsKeys.has(event.slug) : false
  }

  /**
   * Filter live and planned events
   * @param filterBy Filter criteria
   */
  filter(filterBy: string): void {
    if (filterBy === 'All') {
      this.liveAndplannedEvents = [...this.allEvents]
      this.paymentFilter = ''
      this.currentFilter = 'All'
      return
    }

    if (filterBy === 'payment') {
      this.handlePaymentFilter()
      return
    }

    this.currentFilter = filterBy
    this.liveAndplannedEvents = this.allEvents.filter((record) => record.status === filterBy)
  }

  /**
   * Handle payment filter sorting
   */
  private handlePaymentFilter(): void {
    this.paymentFilter = this.paymentFilter === 'yes' ? 'no' : 'yes'
    const sortDirection = this.paymentFilter === 'yes' ? -1 : 1

    this.liveAndplannedEvents.sort((a, b) => {
      const x = a.collect_payment.toLowerCase()
      const y = b.collect_payment.toLowerCase()
      return x > y ? sortDirection : x < y ? -sortDirection : 0
    })
  }

  /**
   * Fetches the gender list from the server and updates the component's state.
   * @param page The page number to fetch from the server. Defaults to 1.
   */
  getGenderList(page = 1) {
    this.service
      .genderList(this.itemsPerPage, page)
      .pipe(takeUntil(this._destroy$))
      .subscribe((response) => {
        this.originalGenderList = response.data
        this.genderList = [...this.originalGenderList]
        this.currentPage = response.current_page
        this.lastPage = response.last_page
      })
  }

  /**
   * Changes the current page and refreshes the gender list.
   * @param page The page number to navigate to.
   */
  changePage(page: number) {
    this.currentPage = page
    this.getGenderList(page)
  }

  /**
   * Opens the Add Name dialog and updates the gender list upon successful addition.
   */
  openAddNameDialog(): void {
    const dialogRef = this.dialog.open(AddNameDialogComponent)

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (result) {
          this.service
            .createName(result.name, result.gender)
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
              this.toastr.success(`You've added a ${result.name} the gender is ${result.gender} successfully`, '')
              this.getGenderList()
            })
        }
      })
  }

  /**
   * Searches the gender list based on the provided search term.
   * @param searchTerm The term to search the gender list for.
   */
  searchGenderList(searchTerm: string) {
    if (searchTerm) {
      this.service
        .searchGenderList(searchTerm)
        .pipe(takeUntil(this._destroy$))
        .subscribe((response) => {
          if (response.data.length > 0) {
            this.originalGenderList = response.data
            this.genderList = [...this.originalGenderList]
            this.currentPage = response.current_page
            this.lastPage = response.last_page
          } else {
            this.toastr.error(`No matching record for ${searchTerm}`, '')
          }
        })
    } else {
      // If there's no search term, reset the genderList to the original list
      this.getGenderList()
    }
  }

  /**
   * Updates a name in the gender list.
   * @param index The index of the name to update in the gender list.
   * @param newName The new name to replace the old one.
   */
  updateName(index: number, newName: string) {
    this.service
      .updateGenderList(newName, this.genderList[index].gender, this.genderList[index].id)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        () => {
          this.toastr.success(`You've updated a name successfully. Updated name is : ${newName}`, '')
          this.getGenderList()
        },
        (error) => this.toastr.error('Error updating name', error.message)
      )
  }

  /**
   * Discards the delete operation and hides the delete confirmation.
   */
  discardDelete() {
    this.showDeleteConfirmation = false
    this.itemToDelete = undefined
  }

  /**
   * Opens the Delete Confirmation dialog and deletes a name upon confirmation.
   * @param index The index of the name to delete in the gender list.
   */
  confirmDelete(index: number) {
    const dialogData: DialogData = {
      headerText: `Delete a record`,
      messageText: `Are you sure you want to delete ${this.genderList[index].first_name}?`,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Delete',
      confirmButtonClass: 'delete'
    }

    const dialogRef = this.dialog.open(DialogComponent, {
      minWidth: '300px',
      panelClass: 'matdialog',
      data: dialogData
    })

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (result) {
          this.deleteName(index)
        }
      })
  }

  /**
   * Deletes a name from the gender list.
   * @param index The index of the name to delete in the gender list.
   */
  deleteName(index: number) {
    const id = this.genderList[index].id
    this.service
      .deleteGenderRecord(id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((response) => {
        this.toastr.success(`You've deleted ${this.genderList[index].first_name}`, '')
        this.getGenderList()
      })
  }

  onNameBlur(newName: string, index: number) {
    if (newName === this.genderList[index].first_name) return

    this.updateName(index, newName)
  }

  /**
   * Edits the gender of a name in the gender list.
   * @param record The record containing the name whose gender is to be edited.
   * @param gender The new gender to assign to the name.
   */
  editGender(record, gender: string) {
    const index = this.genderList.findIndex((item) => item.id === record.id)

    if (index !== -1) {
      // Set gender
      this.genderList[index].gender = gender
      this.service
        .updateGenderList(this.genderList[index].first_name, gender, this.genderList[index].id)
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          () => this.toastr.success(`You've assigned a new gender successfully. Gender now is ${gender}`, ''),
          (error) => this.toastr.error('Error updating gender', error.message)
        )
    }
  }

  /**
   * Calculate total SMS cost for all events
   * @returns Total SMS cost formatted to 2 decimal places
   */
  getTotalSmsCost(): string {
    if (!this.smsEvents || this.smsEvents.length === 0) {
      return '0.00'
    }
    const total = this.smsEvents.reduce((sum, event) => sum + parseFloat(event.sms_cost), 0)
    return total.toFixed(2)
  }
}
